import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCartDisplayName } from '../utils/cartLine';
import { formatDeliveryAddress, isDeliveryAddressComplete } from '../utils/deliveryAddress';
import { Trash2, Plus, Minus, Clock, Landmark, IdCard, MapPin, Loader2, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

import { getApiUrl, isBackendConfigured } from '../config';
// Tribal land / reservation sales are tax-exempt.
const TAX_RATE = 0;
const STORE_ADDRESS = '42 Montauk Hwy, Southampton, NY 11968';

const CheckoutPage = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const { customer, isAuthenticated, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Cart, 2: Delivery & Payment

  // Check authentication on mount
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Redirect to login with return URL
      navigate('/customer/login', { state: { from: '/checkout' } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Form state
  const [fulfillmentType, setFulfillmentType] = useState('delivery'); // delivery | curbside
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('NY');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliverySchedule, setDeliverySchedule] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  
  // Payment form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [cardError, setCardError] = useState('');
  const [orderError, setOrderError] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false);
  const [paymentConfigured, setPaymentConfigured] = useState(true);

  const isCurbside = fulfillmentType === 'curbside';
  const subtotal = getCartTotal();
  const deliveryFee = deliveryQuote?.deliveryFee ?? null;
  const tax = subtotal * TAX_RATE;
  const total = deliveryFee === null ? null : subtotal + deliveryFee + tax;

  const fullDeliveryAddress = useMemo(
    () =>
      isCurbside
        ? STORE_ADDRESS
        : formatDeliveryAddress(deliveryStreet, deliveryCity, deliveryState, deliveryZip),
    [isCurbside, deliveryStreet, deliveryCity, deliveryState, deliveryZip]
  );

  useEffect(() => {
    if (!isBackendConfigured()) return undefined;
    axios
      .get(getApiUrl('/api/payments/config'))
      .then((res) => setPaymentConfigured(res.data.configured !== false))
      .catch(() => setPaymentConfigured(false));
  }, []);

  useEffect(() => {
    if (step !== 2) {
      return undefined;
    }

    const addressReady = isCurbside
      ? true
      : isDeliveryAddressComplete(deliveryStreet, deliveryCity, deliveryState, deliveryZip);

    if (!addressReady) {
      setDeliveryQuote(null);
      setDeliverySchedule(null);
      setQuoteError('');
      setDeliveryDate('');
      setDeliveryTime('');
      return undefined;
    }

    if (!isBackendConfigured()) {
      setQuoteError('Scheduling unavailable. Backend URL is not configured.');
      return undefined;
    }

    const timer = setTimeout(async () => {
      setQuoteLoading(true);
      setScheduleLoading(true);
      setQuoteError('');
      try {
        const [quoteRes, scheduleRes] = await Promise.all([
          axios.post(getApiUrl('/api/delivery/quote'), {
            address: fullDeliveryAddress,
            fulfillmentType,
          }),
          axios.post(getApiUrl('/api/delivery/availability'), {
            address: fullDeliveryAddress,
            fulfillmentType,
          }),
        ]);
        setDeliveryQuote(quoteRes.data);
        setDeliverySchedule(scheduleRes.data);
      } catch (err) {
        setDeliveryQuote(null);
        setDeliverySchedule(null);
        setQuoteError(
          err.response?.data?.detail ||
            (isCurbside
              ? 'Could not load curbside pickup times.'
              : 'Could not calculate delivery options for this address.')
        );
      } finally {
        setQuoteLoading(false);
        setScheduleLoading(false);
      }
    }, isCurbside ? 100 : 600);

    return () => clearTimeout(timer);
  }, [
    fulfillmentType,
    isCurbside,
    deliveryStreet,
    deliveryCity,
    deliveryState,
    deliveryZip,
    fullDeliveryAddress,
    step,
  ]);

  const availableDates = deliverySchedule?.days?.filter((day) => day.hasAvailability) ?? [];
  const selectedDay = deliverySchedule?.days?.find((day) => day.date === deliveryDate);
  const timeSlots = selectedDay?.slots ?? [];
  const selectedSlot = timeSlots.find((slot) => slot.value === deliveryTime);

  const getDeliveryPayload = (slot = selectedSlot) => ({
    fulfillmentType,
    date: deliveryDate,
    time: deliveryTime,
    timeLabel: slot?.label,
    estimatedArrival: slot?.estimatedArrival,
    street: isCurbside ? '42 Montauk Hwy' : deliveryStreet.trim(),
    city: isCurbside ? 'Southampton' : deliveryCity.trim(),
    state: isCurbside ? 'NY' : deliveryState.trim().toUpperCase(),
    zip: isCurbside ? '11968' : deliveryZip.trim(),
    address: fullDeliveryAddress,
    notes: deliveryNotes,
  });

  // Sync default date/time when schedule loads or the selected date changes — not when the user picks a time.
  useEffect(() => {
    if (!deliverySchedule?.days?.length) return;

    const dates = deliverySchedule.days.filter((day) => day.hasAvailability);
    if (!dates.length) {
      setDeliveryDate('');
      setDeliveryTime('');
      return;
    }

    const dateIsValid = deliveryDate && dates.some((day) => day.date === deliveryDate);
    const nextDate = dateIsValid ? deliveryDate : dates[0].date;

    if (nextDate !== deliveryDate) {
      setDeliveryDate(nextDate);
      return;
    }

    const day = deliverySchedule.days.find((d) => d.date === nextDate);
    const slots = day?.slots ?? [];
    if (!slots.length) {
      setDeliveryTime('');
      return;
    }

    setDeliveryTime((current) =>
      slots.some((slot) => slot.value === current) ? current : slots[0].value
    );
  }, [deliverySchedule, deliveryDate]);

  useEffect(() => {
    setDeliveryDate('');
    setDeliveryTime('');
    setDeliveryQuote(null);
    setDeliverySchedule(null);
    setQuoteError('');
  }, [fulfillmentType]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  // If not authenticated, don't render (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Fallback date list while schedule is loading
  const getFallbackDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        label:
          i === 0
            ? 'Today'
            : i === 1
              ? 'Tomorrow'
              : date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                }),
      });
    }
    return dates;
  };

  const dateOptions = availableDates.length ? availableDates : getFallbackDates();

  const formatDriverFreeTime = (isoString) => {
    if (!isoString) return null;
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const validateDebitCard = async (number) => {
    const digits = number.replace(/\D/g, '');
    if (digits.length < 13) {
      setCardError('');
      return;
    }
    if (!isBackendConfigured()) return;
    try {
      await axios.post(getApiUrl('/api/payments/validate-card'), { cardNumber: digits });
      setCardError('');
    } catch (err) {
      setCardError(err.response?.data?.detail || 'Only debit cards are accepted.');
    }
  };

  const reportCheckoutFailure = async (reason, statusCode = 400) => {
    if (!isBackendConfigured()) return;
    const digits = cardNumber.replace(/\D/g, '');
    try {
      await axios.post(getApiUrl('/api/orders/checkout-failure'), {
        reason,
        source: 'frontend',
        statusCode,
        customerId: customer?.id || null,
        customerEmail: customer?.email || null,
        customerFirstName: customer?.firstName || null,
        customerLastName: customer?.lastName || null,
        customerPhone: customer?.phone || null,
        items: cartItems.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        delivery: getDeliveryPayload(),
        total,
        paymentLastFour: digits.length >= 4 ? digits.slice(-4) : null,
      });
    } catch {
      // Best-effort alert only
    }
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setOrderError('');

    if (cardError) {
      setOrderError(cardError);
      await reportCheckoutFailure(cardError);
      return;
    }
    
    // Validate all fields
    if (
      !deliveryDate ||
      !deliveryTime ||
      (!isCurbside &&
        !isDeliveryAddressComplete(deliveryStreet, deliveryCity, deliveryState, deliveryZip)) ||
      !cardNumber ||
      !cardName ||
      !expiryDate ||
      !cvv ||
      !billingZip
    ) {
      const reason = isCurbside
        ? 'Please fill in all required fields (including pickup date and time)'
        : 'Please fill in all required fields (including city, state, and ZIP)';
      setOrderError(reason);
      await reportCheckoutFailure(reason);
      return;
    }

    if (!paymentConfigured) {
      const reason = 'Payment processing is temporarily unavailable. Please try again later.';
      setOrderError(reason);
      await reportCheckoutFailure(reason, 503);
      return;
    }

    if (deliveryFee === null || quoteLoading || scheduleLoading) {
      const reason = isCurbside
        ? 'Please wait for curbside pickup times to load.'
        : 'Please wait for delivery pricing and available times to load.';
      setOrderError(reason);
      await reportCheckoutFailure(reason);
      return;
    }

    if (!deliveryTime || !timeSlots.length) {
      const reason = isCurbside
        ? 'No curbside pickup times are available for this date. Try another date.'
        : 'No delivery times are available for this address and date. Try another date.';
      setOrderError(reason);
      await reportCheckoutFailure(reason);
      return;
    }

    setPlacingOrder(true);

    // Create order object
    const orderData = {
      items: cartItems.map((item) => ({
        id: item.id,
        name: getCartDisplayName(item),
        category: item.category,
        price: item.price,
        quantity: item.quantity,
        thc: item.thc,
        image: item.image,
        variantId: item.selectedVariant?.id || null,
        variantName: item.selectedVariant?.name || null,
        backupVariantId: item.backupVariant?.id || null,
        backupVariantName: item.backupVariant?.name || null,
      })),
      subtotal,
      deliveryFee,
      tax,
      total,
      delivery: getDeliveryPayload(),
      payment: {
        cardNumber,
        cardName,
        expiryDate,
        cvv,
        billingZip
      },
      customerId: customer?.id || null,
      customerEmail: customer?.email || null,
      customerFirstName: customer?.firstName || null,
      customerLastName: customer?.lastName || null,
      customerPhone: customer?.phone || null,
    };

    try {
      const response = await axios.post(getApiUrl('/api/orders'), orderData);
      
      // Store order in localStorage for confirmation page
      localStorage.setItem('tribaldash_last_order', JSON.stringify(response.data));
      
      // Clear cart
      clearCart();
      
      // Navigate to confirmation
      navigate('/order-confirmation');
    } catch (error) {
      console.error('Error placing order:', error);
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => item?.msg || JSON.stringify(item)).join(' ')
        : typeof detail === 'string'
          ? detail.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
          : '';
      setOrderError(
        message || 'Payment or order failed. Please check your debit card and try again.'
      );
      // Backend sends failure alert for API/order errors.
    } finally {
      setPlacingOrder(false);
    }
  };

  if (cartItems.length === 0 && step === 1) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-neutral-900 mb-4">Your Cart is Empty</h1>
          <p className="text-gray-600 mb-8">Add some products to your cart to continue</p>
          <Button
            onClick={() => navigate('/menu')}
            className="bg-neutral-950 hover:bg-black"
          >
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-neutral-900 mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 1 ? 'bg-neutral-950 text-white' : 'bg-neutral-300 text-neutral-600'
            }`}>
              1
            </div>
            <span className="ml-2 font-medium">Cart</span>
          </div>
          <div className="w-24 h-1 bg-gray-300 mx-4" />
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 2 ? 'bg-neutral-950 text-white' : 'bg-neutral-300 text-neutral-600'
            }`}>
              2
            </div>
            <span className="ml-2 font-medium">Delivery & Payment</span>
          </div>
        </div>

        <Alert
          className="mb-8 border-neutral-300 bg-neutral-100 text-neutral-900 [&>svg]:text-neutral-700"
          role="status"
        >
          <IdCard className="h-4 w-4" aria-hidden />
          <AlertDescription>
            <strong className="font-semibold">ID required at delivery.</strong>{' '}
            A valid government-issued photo ID must be shown when your order arrives. The person receiving
            the delivery must be 21 or older.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 ? (
              /* Cart Items */
              <Card>
                <CardHeader>
                  <CardTitle>Shopping Cart ({cartItems.length} items)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cartItems.map((item) => (
                      <div key={item.cartKey || item.id} className="flex items-center gap-4 p-4 border rounded-lg">
                        <img
                          src={item.image}
                          alt={getCartDisplayName(item)}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-neutral-900">{getCartDisplayName(item)}</h3>
                          <p className="text-sm text-gray-600">{item.category}</p>
                          <p className="text-sm font-semibold text-neutral-700 mt-1">THC: {item.thc}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateQuantity(item.cartKey || item.id, item.quantity - 1)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-semibold">{item.quantity}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateQuantity(item.cartKey || item.id, item.quantity + 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-neutral-900">${(item.price * item.quantity).toFixed(2)}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFromCart(item.cartKey || item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-2"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <Button
                      onClick={() => setStep(2)}
                      className="w-full bg-neutral-950 hover:bg-black"
                      size="lg"
                    >
                      Continue to Checkout
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Delivery & Payment Form */
              <form onSubmit={handlePlaceOrder}>
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {isCurbside ? <Car className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                      {isCurbside ? 'Curbside Pickup' : 'Delivery Information'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="mb-2 block">How would you like to get your order? *</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFulfillmentType('delivery')}
                          className={`rounded-lg border-2 px-4 py-3 text-left transition-colors ${
                            fulfillmentType === 'delivery'
                              ? 'border-neutral-900 bg-neutral-50'
                              : 'border-neutral-200 hover:border-neutral-400'
                          }`}
                        >
                          <p className="font-semibold flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Delivery
                          </p>
                          <p className="text-sm text-neutral-600 mt-1">
                            Same-day delivery across The Hamptons
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFulfillmentType('curbside')}
                          className={`rounded-lg border-2 px-4 py-3 text-left transition-colors ${
                            fulfillmentType === 'curbside'
                              ? 'border-neutral-900 bg-neutral-50'
                              : 'border-neutral-200 hover:border-neutral-400'
                          }`}
                        >
                          <p className="font-semibold flex items-center gap-2">
                            <Car className="w-4 h-4" />
                            Curbside Pickup
                          </p>
                          <p className="text-sm text-neutral-600 mt-1">
                            Pick up at Cloud 9 — no delivery fee
                          </p>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="delivery-date">
                          {isCurbside ? 'Pickup Date *' : 'Delivery Date *'}
                        </Label>
                        <Select
                          value={deliveryDate || undefined}
                          onValueChange={setDeliveryDate}
                          required
                          disabled={scheduleLoading || !availableDates.length}
                        >
                          <SelectTrigger id="delivery-date">
                            <SelectValue
                              placeholder={
                                scheduleLoading ? 'Loading dates...' : 'Select date'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {dateOptions.map((day) => (
                              <SelectItem key={day.date} value={day.date}>
                                {day.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="delivery-time">
                          {isCurbside ? 'Pickup Time *' : 'Delivery Time *'}
                        </Label>
                        <Select
                          value={deliveryTime || undefined}
                          onValueChange={setDeliveryTime}
                          required
                          disabled={scheduleLoading || !timeSlots.length}
                        >
                          <SelectTrigger id="delivery-time">
                            <SelectValue
                              placeholder={
                                scheduleLoading
                                  ? 'Calculating times...'
                                  : timeSlots.length
                                    ? 'Select time'
                                    : 'No times available'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((slot) => (
                              <SelectItem key={slot.value} value={slot.value}>
                                {slot.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {(scheduleLoading || quoteLoading) && (
                      <p className="text-sm text-neutral-600 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isCurbside
                          ? 'Loading curbside pickup windows...'
                          : 'Checking driver availability and travel time from Cloud 9...'}
                      </p>
                    )}
                    {deliverySchedule && !scheduleLoading && isCurbside && (
                      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800 space-y-1">
                        <p>
                          Curbside pickup at <strong>{STORE_ADDRESS}</strong>. Pull up when your
                          window starts and we&apos;ll bring your order out.
                        </p>
                        {selectedSlot && (
                          <p className="text-neutral-600">Selected window: {selectedSlot.label}</p>
                        )}
                        {!availableDates.length && (
                          <p className="text-red-600">
                            No curbside pickup windows available in the next 7 days.
                          </p>
                        )}
                      </div>
                    )}
                    {deliverySchedule && !scheduleLoading && !isCurbside && (
                      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800 space-y-1">
                        <p>
                          <strong>~{deliverySchedule.travelMinutes} min</strong> round-trip from
                          the store for your address ({deliverySchedule.deliveryMiles} mi).
                        </p>
                        {deliverySchedule.driversOnRoute > 0 ? (
                          <p>
                            {deliverySchedule.driversOnRoute} driver
                            {deliverySchedule.driversOnRoute === 1 ? ' is' : 's are'} currently
                            out — next availability from{' '}
                            <strong>
                              {formatDriverFreeTime(deliverySchedule.earliestDriverAvailable)}
                            </strong>
                            .
                          </p>
                        ) : (
                          <p>A driver is available to dispatch from the store.</p>
                        )}
                        {selectedSlot && (
                          <p className="text-neutral-600">
                            Selected window: {selectedSlot.label}
                          </p>
                        )}
                        {!availableDates.length && (
                          <p className="text-red-600">
                            No delivery windows available in the next 7 days for this route.
                          </p>
                        )}
                      </div>
                    )}
                    {!isCurbside && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="delivery-street">Street Address *</Label>
                        <Input
                          id="delivery-street"
                          placeholder="35 Washington Heights Ave"
                          value={deliveryStreet}
                          onChange={(e) => setDeliveryStreet(e.target.value)}
                          autoComplete="address-line1"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-1">
                          <Label htmlFor="delivery-city">City *</Label>
                          <Input
                            id="delivery-city"
                            placeholder="Southampton"
                            value={deliveryCity}
                            onChange={(e) => setDeliveryCity(e.target.value)}
                            autoComplete="address-level2"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="delivery-state">State *</Label>
                          <Input
                            id="delivery-state"
                            placeholder="NY"
                            value={deliveryState}
                            onChange={(e) => setDeliveryState(e.target.value.toUpperCase().slice(0, 2))}
                            autoComplete="address-level1"
                            maxLength={2}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="delivery-zip">ZIP *</Label>
                          <Input
                            id="delivery-zip"
                            placeholder="11968"
                            value={deliveryZip}
                            onChange={(e) => setDeliveryZip(e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
                            autoComplete="postal-code"
                            inputMode="numeric"
                            required
                          />
                        </div>
                      </div>
                      {fullDeliveryAddress && (
                        <p className="text-sm text-neutral-600">
                          Deliver to: <span className="font-medium text-neutral-900">{fullDeliveryAddress}</span>
                        </p>
                      )}
                      {quoteLoading && (
                        <p className="mt-2 text-sm text-neutral-600 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Calculating delivery distance from Cloud 9...
                        </p>
                      )}
                      {deliveryQuote && !quoteLoading && (
                        <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                              <p className="font-semibold">
                                {deliveryQuote.deliveryMiles} miles from store · $
                                {deliveryQuote.deliveryFee.toFixed(2)} delivery fee
                              </p>
                              <p className="text-green-800 mt-1">
                                ${deliveryQuote.baseFee.toFixed(0)} base
                                {deliveryQuote.mileageFee > 0
                                  ? ` + $${deliveryQuote.mileageFee.toFixed(2)} (${deliveryQuote.additionalMiles.toFixed(1)} mi after first mile)`
                                  : ' (first mile included)'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {quoteError && !quoteLoading && (
                        <p className="mt-2 text-sm text-red-600">{quoteError}</p>
                      )}
                    </div>
                    )}
                    {isCurbside && quoteError && !quoteLoading && (
                      <p className="text-sm text-red-600">{quoteError}</p>
                    )}
                    <div>
                      <Label htmlFor="delivery-notes">
                        {isCurbside ? 'Pickup Notes (Optional)' : 'Delivery Notes (Optional)'}
                      </Label>
                      <Input
                        id="delivery-notes"
                        placeholder={
                          isCurbside
                            ? 'Car make/model/color, or special instructions'
                            : 'Any special instructions?'
                        }
                        value={deliveryNotes}
                        onChange={(e) => setDeliveryNotes(e.target.value)}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="w-5 h-5" />
                      Debit Card Payment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Alert className="border-neutral-300 bg-neutral-50 text-neutral-900">
                      <AlertDescription>
                        <strong className="font-semibold">Debit cards only.</strong> Credit cards are
                        not accepted. Your card is processed securely through Cloud 9 ProteusERP at
                        checkout.
                      </AlertDescription>
                    </Alert>
                    {!paymentConfigured && (
                      <Alert className="border-red-300 bg-red-50 text-red-900">
                        <AlertDescription>
                          Payment processing is not configured yet. Orders cannot be placed until
                          ProteusERP credentials are set on the server.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div>
                      <Label htmlFor="card-number">Debit card number *</Label>
                      <Input
                        id="card-number"
                        placeholder="1234 5678 9012 3456"
                        value={cardNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s/g, '');
                          setCardNumber(value);
                          if (cardError) setCardError('');
                        }}
                        onBlur={() => validateDebitCard(cardNumber)}
                        maxLength="19"
                        inputMode="numeric"
                        required
                      />
                      {cardError && <p className="mt-2 text-sm text-red-600">{cardError}</p>}
                    </div>
                    <div>
                      <Label htmlFor="card-name">Cardholder Name *</Label>
                      <Input
                        id="card-name"
                        placeholder="John Doe"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-1">
                        <Label htmlFor="expiry">Expiry *</Label>
                        <Input
                          id="expiry"
                          placeholder="MM/YY"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          maxLength="5"
                          required
                        />
                      </div>
                      <div className="col-span-1">
                        <Label htmlFor="cvv">CVV *</Label>
                        <Input
                          id="cvv"
                          placeholder="123"
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value)}
                          maxLength="4"
                          required
                        />
                      </div>
                      <div className="col-span-1">
                        <Label htmlFor="zip">Billing ZIP *</Label>
                        <Input
                          id="zip"
                          placeholder="10001"
                          value={billingZip}
                          onChange={(e) => setBillingZip(e.target.value)}
                          maxLength="5"
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {orderError && (
                  <div className="mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
                    {orderError}
                  </div>
                )}

                <div className="flex gap-4 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1"
                  >
                    Back to Cart
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    size="lg"
                    disabled={
                      placingOrder ||
                      quoteLoading ||
                      scheduleLoading ||
                      deliveryFee === null ||
                      !!quoteError ||
                      !deliveryTime ||
                      !paymentConfigured ||
                      !!cardError
                    }
                  >
                    {placingOrder
                      ? 'Processing debit payment...'
                      : total === null
                        ? isCurbside
                          ? 'Loading pickup times...'
                          : 'Enter address for total'
                        : `Place Order - $${total.toFixed(2)}`}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-neutral-700">
                    <span>Subtotal:</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-700">
                    <span>{isCurbside ? 'Pickup Fee:' : 'Delivery Fee:'}</span>
                    <span>
                      {quoteLoading
                        ? 'Calculating...'
                        : deliveryFee === null
                          ? isCurbside
                            ? '—'
                            : 'Enter address'
                          : deliveryFee === 0
                            ? 'FREE'
                            : `$${deliveryFee.toFixed(2)}`}
                    </span>
                  </div>
                  {deliveryQuote && (
                    <p className="text-xs text-neutral-500">
                      {isCurbside
                        ? deliveryQuote.rateDescription
                        : `${deliveryQuote.deliveryMiles} mi from 42 Montauk Hwy, Southampton · ${deliveryQuote.rateDescription}`}
                    </p>
                  )}
                  {quoteError && step === 2 && (
                    <p className="text-xs text-red-600">{quoteError}</p>
                  )}
                  <div className="flex justify-between text-neutral-700">
                    <span>Tax:</span>
                    <span>Exempt</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold text-neutral-900">
                      <span>Total:</span>
                      <span>{total === null ? '—' : `$${total.toFixed(2)}`}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
